import { Injectable, inject, signal } from '@angular/core';
import { ApiService } from './api.service';

export type AutorizacionStatus = 'pendiente' | 'aprobada' | 'rechazada' | 'usada' | 'expirada';

export interface Autorizacion {
  id: string;
  adminId: string;
  adminNombre: string;
  turnoId?: string;
  bodegaNombre?: string;
  accion: string;
  contexto: Record<string, any>;
  status: AutorizacionStatus;
  solicitadaEn: string;
  resueltaEn?: string;
  expiraEn?: string;
}

type Row = { [key: string]: any };

@Injectable({ providedIn: 'root' })
export class AutorizacionesService {
  private api = inject(ApiService);

  autorizaciones = signal<Autorizacion[]>([]);
  loading = signal(false);
  error = signal('');

  private mapRow(r: Row): Autorizacion {
    let contexto = r['contexto'];
    if (typeof contexto === 'string') {
      try { contexto = JSON.parse(contexto); } catch { contexto = {}; }
    }
    return {
      id: r['id'],
      adminId: r['admin_id'],
      adminNombre: r['admin_nombre'],
      turnoId: r['turno_id'] ?? undefined,
      bodegaNombre: r['bodega_nombre'] ?? undefined,
      accion: r['accion'],
      contexto: contexto ?? {},
      status: r['status'],
      solicitadaEn: r['solicitada_en'],
      resueltaEn: r['resuelta_en'] ?? undefined,
      expiraEn: r['expira_en'] ?? undefined,
    };
  }

  async load(status?: AutorizacionStatus) {
    this.loading.set(true);
    this.error.set('');
    try {
      const params: Record<string, string> = status ? { status } : {};
      const res: any = await this.api.get('/cp/autorizaciones', params);
      this.autorizaciones.set(((res.autorizaciones ?? []) as Row[]).map(r => this.mapRow(r)));
    } catch (e: any) {
      this.error.set(e?.error?.error ?? e?.message ?? 'Error al cargar autorizaciones.');
    }
    this.loading.set(false);
  }

  async resolver(id: string, decision: 'aprobar' | 'rechazar'): Promise<{ ok: boolean; codigo?: string; error?: string }> {
    try {
      const res: any = await this.api.post('/cp/autorizaciones/resolver', { id, decision });
      // Se quita localmente de la lista de pendientes en vez de recargar sin
      // filtro (que traería también la que se acaba de resolver).
      this.autorizaciones.set(this.autorizaciones().filter(a => a.id !== id));
      return { ok: true, codigo: res.codigo };
    } catch (e: any) {
      return { ok: false, error: e?.error?.error ?? e?.message ?? 'Error' };
    }
  }
}
